<template>
  <div class="roster-page">
 
    <!-- 加载遮罩 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p>Loading design...</p>
      </div>
    </div>
 
    <!-- Toast 通知 -->
    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>
 
    <div class="container">
      <div class="page-layout">
 
        <!-- 左侧：3D 只读预览 -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>
 
        <!-- 右侧面板 -->
        <div class="right-panel">
 
          <!-- 款式选择（只读，点击切换3D预览） -->
          <div class="panel-block">
            <ProductGrid @select-garment="handleSelectGarment" />
          </div>
 
          <!-- 名单管理 -->
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" :show-type="true" />
          </div>
 
        </div>
      </div>
    </div>
  </div>
</template>
 
<script>
import { useDesignStore } from '@/stores/designStore'
import { mapActions } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import ProductGrid from '@/components/ProductGrid.vue'
import RosterManager from '@/components/RosterManager.vue'
 
export default {
  name: 'RosterPage',
 
  components: { Viewer3D, ProductGrid, RosterManager },
 
  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
      _pendingDesignResult: null,
    }
  },
 
  mounted() {
    this.init()
  },
 
  methods: {
    ...mapActions(useDesignStore, ['switchGarment']), 
    async init() {
      const params = new URLSearchParams(window.location.search)
      const id = params.get('id')
      if (!id) return

      this.isLoading = true
      const store = useDesignStore()

      try {
        // 1. 直接 fetch 数据，手动写入 store，完全控制时序
        const res = await fetch(`/load-design.php?id=${id}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const result = await res.json()
        if (!result.success || !result.all_garments) throw new Error('Design not found')

        store.currentDesignId = result.design_id || id
        const targetGarment = result.current_garment || 'Short Sleeve'
        const garmentData = result.all_garments[targetGarment] || {}

        // 2. 清空旧数据，写入正确款式的数据
        store.userColors.clear()
        store.partLayersMap.clear()
        store.textItems = []
        store.currentGarment = targetGarment

        // 恢复颜色
        if (garmentData.colors) {
          for (const [part, colorHex] of Object.entries(garmentData.colors)) {
            const colorNum = typeof colorHex === 'string'
              ? parseInt(colorHex.replace('#', ''), 16) : colorHex
            store.userColors.set(part, colorNum)
          }
        }

        // 恢复 logos
        if (garmentData.logos) {
          for (const logo of garmentData.logos) {
            const layers = store.partLayersMap.get(logo.part) || []
            layers.push({
              id: logo.id, url: logo.url, isText: false,
              scale: logo.scale || 1, offsetX: logo.offsetX || 0,
              offsetY: logo.offsetY || 0, rotation: logo.rotation || 0,
              img: null
            })
            store.partLayersMap.set(logo.part, layers)
          }
        }

        // 恢复文字
        if (garmentData.texts) {
          for (const text of garmentData.texts) {
            const layers = store.partLayersMap.get(text.part) || []
            layers.push({
              id: text.id, isText: true,
              textContent: text.text, textFont: text.font,
              textColor: text.color, fontSize: text.fontSize || 80,
              scale: text.scale || 0.8, offsetX: text.offsetX || 0,
              offsetY: text.offsetY || 0, rotation: text.rotation || 0,
              img: null
            })
            store.partLayersMap.set(text.part, layers)
          }
        }

        // 恢复 roster
        store.rosterList = (result.roster || []).map(m => ({
          name: m.name || '', gender: m.gender || 'Male',
          style: targetGarment,
          size: m.size != null ? m.size : '',
          quantity: m.quantity != null && m.quantity !== '' ? m.quantity : '',
          message: m.message || ''
        }))

        // 3. 加载 3D 模型（onModelLoaded 完成后合成纹理）
        await this.$nextTick()
        if (this.$refs.viewer3d) {
          this.$refs.viewer3d.loadGarment(targetGarment)
        }

        // 4. 刷新 roster 列表
        await this.$nextTick()
        if (this.$refs.rosterManager && typeof this.$refs.rosterManager.loadFromStore === 'function') {
          this.$refs.rosterManager.loadFromStore()
        }

      } catch (e) {
        console.error('Load error:', e)
        this.showToast('Failed to load design', true)
      } finally {
        this.isLoading = false
      }
    },

    // 模型加载完成后，预加载图片再合成纹理
    async onModelLoaded() {
      const store = useDesignStore()
      const viewer = this.$refs.viewer3d
      if (!viewer || !viewer.partsMap || viewer.partsMap.size === 0) return

      if (typeof viewer.applyColorsToModel === 'function') {
        viewer.applyColorsToModel()
      }

      for (const [part, layers] of store.partLayersMap.entries()) {
        if (!layers || layers.length === 0) continue
        if (!viewer.partsMap.has(part)) continue

        for (const layer of layers) {
          if (!layer.isText && layer.url && !layer.img) {
            await new Promise((resolve) => {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.onload  = () => { layer.img = img; resolve() }
              img.onerror = () => resolve()
              img.src = layer.url
            })
          }
          if (layer.isText && layer.textContent && !layer.img) {
            const canvas = store._createTextCanvas(
              layer.textContent, layer.textFont || 'Arial',
              layer.textColor || '#000000', layer.fontSize || 80
            )
            const img = new Image()
            img.src = canvas.toDataURL()
            layer.img = img
          }
        }

        if (typeof viewer.compositePartTexture === 'function') {
          viewer.compositePartTexture(part, null, false)
        }
      }
    },
 
    handleSelectGarment(garmentType) {
      console.log('RosterPage: 切换款式到', garmentType)
      // 更新 store 中的当前款式
      this.switchGarment(garmentType)
      // 通知 Viewer3D 加载新模型
      if (this.$refs.viewer3d) {
        this.$refs.viewer3d.loadGarment(garmentType)
      }
    },
 
    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },
  },
}
</script>
 
<style scoped>
.roster-page {
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #333;
  min-height: 100vh;
}
 
/* ── 加载遮罩 ── */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.loading-inner {
  text-align: center;
  color: #fff;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
 
/* ── Toast ── */
.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  z-index: 10001;
  pointer-events: none;
}
.toast-success { background: #10b981; }
.toast-error   { background: #ef4444; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
 
/* ── 布局 ── */
.container {
  width: 95%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 0;
}
 
.page-layout {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-start;
}
 
.viewer-wrapper {
  flex: 1;
  min-width: 280px;
}
 
.right-panel {
  flex: 1;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}
 
.panel-block {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 1rem;
}
 
.roster-block {
  background: #f0fdf4;
}
 
/* ── 响应式 ── */
@media (max-width: 900px) {
  .page-layout {
    flex-direction: column;
  }
  .viewer-wrapper,
  .right-panel {
    width: 100%;
    min-width: 0;
  }
}
 
@media (max-width: 850px) {
  .container {
    width: 100%;
    padding: 0.5rem;
  }
  .panel-block {
    padding: 0.6rem;
  }
}
</style>